import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Radio, ArrowUpRight, ExternalLink, Loader2, CheckCircle2, TrendingUp, TrendingDown, MinusCircle } from "lucide-react";
import api from "@/lib/api";
import { IMG } from "@/lib/imagery";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";

const PROVIDERS = [
  { id: "pesapal", label: "PesaPal", note: "Cards & mobile money" },
  { id: "nowpayments", label: "NowPayments", note: "BTC · ETH · USDT" },
];

const OUTCOMES = {
  tp: { label: "✅ TP hit", cls: "chip-emerald" },
  sl: { label: "❌ SL hit", cls: "chip-amber" },
  be: { label: "⚪ Breakeven", cls: "chip-neutral" },
  cancelled: { label: "🚫 Cancelled", cls: "chip-neutral" },
};

export default function Signals() {
  const { user } = useAuth();
  const { publicConfig } = useBranding();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [feed, setFeed] = useState({ posts: [], active_sub: false });
  const [feedMore, setFeedMore] = useState(false);
  const [feedBusy, setFeedBusy] = useState(false);
  const [sub, setSub] = useState(null);
  const [provider, setProvider] = useState("pesapal");
  const [busy, setBusy] = useState(false);
  const [trial, setTrial] = useState(null);
  const [trialBusy, setTrialBusy] = useState(false);
  const signalsOff = publicConfig?.signals_enabled === false;

  const loadAll = async () => {
    const [p, f] = await Promise.all([api.get("/signals/plan"), api.get("/signals/examples")]);
    setPlan(p.data);
    setFeed(f.data);
    setFeedMore(!!f.data.has_more);
    if (user) {
      try {
        const { data } = await api.get("/signals/subscription/me");
        setSub(data);
      } catch { /* not subscribed */ }
      try {
        const { data } = await api.get("/signals/trial/status");
        setTrial(data);
      } catch { /* ignore */ }
    }
  };
  useEffect(() => { loadAll(); }, [user]);

  const startTrial = async () => {
    if (!user) { toast.info("Sign in to start your free trial"); navigate("/login"); return; }
    setTrialBusy(true);
    try {
      await api.post("/signals/trial/start");
      toast.success("Free trial activated — enjoy your signals!");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start trial");
    } finally { setTrialBusy(false); }
  };

  const loadMore = async () => {
    setFeedBusy(true);
    try {
      const { data } = await api.get(`/signals/examples?offset=${feed.posts.length}`);
      setFeed((prev) => ({ ...prev, posts: [...prev.posts, ...(data.posts || [])] }));
      setFeedMore(!!data.has_more);
    } catch { /* silent */ }
    finally { setFeedBusy(false); }
  };

  const subscribe = async () => {
    if (!user) {
      toast.info("Sign in to subscribe");
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/signals/subscribe", { provider });
      if (data.checkout_url && /^https?:\/\//.test(data.checkout_url)) {
        window.location.href = data.checkout_url;
      } else {
        navigate(`/checkout/pay/${data.id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
    } finally {
      setBusy(false);
    }
  };

  if (!plan) return <div className="p-24 text-center text-emerald-100/50">Loading…</div>;

  if (signalsOff) {
    return (
      <div className="relative mx-auto max-w-3xl px-5 py-32 text-center lg:px-8" data-testid="signals-coming-soon">
        <p className="overline mb-4">/ signals</p>
        <h1 className="font-editorial text-5xl font-medium tracking-tight text-white sm:text-6xl">Coming soon</h1>
        <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-emerald-100/70">
          Our live signals service is being prepared. Check back shortly — in the meantime, explore our indicators, expert advisors and courses.
        </p>
        <button onClick={() => navigate("/store/expert-advisors")} data-testid="signals-browse-eas" className="btn-edge mt-8">
          Browse Expert Advisors <ArrowUpRight size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[color:var(--ink-hairline)]">
        <div className="pointer-events-none absolute inset-0">
          <img src={IMG.forest} alt="" className="h-full w-full object-cover"
               style={{ filter: "saturate(1.05) contrast(1.02)" }} />
          <div className="absolute inset-0 hero-scrim" />
        </div>
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-5 pt-24 pb-16 lg:px-8 lg:pt-32 lg:pb-24">
          <div className="pl-6 lg:pl-10">
            <div className="vline absolute left-5 top-24 h-20 w-[2px] lg:left-8 lg:top-32" />
            <p className="overline mb-6" data-testid="signals-kicker">/ signals · monthly</p>
            <h1 data-testid="signals-title" className="max-w-3xl font-editorial text-5xl font-medium leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
              Curated setups.
              <br />
              <span className="italic text-emerald-300">Delivered live.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-emerald-100/70">
              {plan.description}
            </p>
          </div>
        </div>
      </section>

      {/* Plan + subscribe */}
      <section className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
        <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:gap-14">
          <div className="card-moss relative overflow-hidden p-8 lg:p-10">
            <div className="mb-6 flex items-center justify-between">
              <span className="chip chip-lime">{sub?.active ? "You’re subscribed" : "Monthly plan"}</span>
              <Radio size={18} className="text-lime-300" />
            </div>
            <p className="font-mono-edge text-[10px] uppercase tracking-widest text-emerald-100/50">Price</p>
            <p className="mt-1 font-editorial text-6xl text-white">
              ${Number(plan.price).toFixed(0)}
              <span className="ml-2 text-lg text-emerald-100/50">/ month</span>
            </p>
            <ul className="mt-8 space-y-2.5 text-sm">
              {[
                "Private Telegram channel access",
                "On-site signal feed mirror",
                "Auto-revoke on expiry — cancel anytime",
                "Priority support",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 text-emerald-100/85">
                  <CheckCircle2 size={14} className="mt-1 text-lime-300" /> {f}
                </li>
              ))}
            </ul>

            {sub?.active ? (
              <div className="mt-8 grid gap-3">
                <p className="text-sm text-emerald-100/70">
                  Active until <span className="font-mono-edge text-emerald-200">{new Date(sub.current_period_end).toLocaleString()}</span>
                </p>
                {sub.telegram_invite_url && (
                  <a href={sub.telegram_invite_url} target="_blank" rel="noreferrer" data-testid="signals-telegram-link" className="btn-edge">
                    Join Telegram <ExternalLink size={14} />
                  </a>
                )}
              </div>
            ) : (
              <>
                <div className="mt-8">
                  <p className="overline mb-3">Provider</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {PROVIDERS.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setProvider(p.id)}
                        data-testid={`signals-provider-${p.id}`}
                        className={`rounded-xl border p-3 text-left transition-colors ${
                          provider === p.id
                            ? "border-lime-400/60 bg-emerald-500/10"
                            : "border-emerald-500/15 bg-[#0d2416]/50 hover:border-emerald-500/40"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-display text-sm font-medium text-white">{p.label}</span>
                          <span className={`h-2.5 w-2.5 rounded-full ${provider === p.id ? "bg-lime-300" : "bg-emerald-500/30"}`} />
                        </div>
                        <p className="mt-1 text-[11px] text-emerald-100/60">{p.note}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  onClick={subscribe}
                  disabled={busy}
                  data-testid="signals-subscribe"
                  className="btn-edge mt-6 w-full justify-center"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <>Subscribe for ${Number(plan.price).toFixed(0)}/mo <ArrowUpRight size={14} /></>}
                </button>
                {trial?.available && (
                  <button
                    onClick={startTrial}
                    disabled={trialBusy}
                    data-testid="signals-start-trial"
                    className="btn-edge-ghost mt-3 w-full justify-center"
                  >
                    {trialBusy ? <Loader2 size={16} className="animate-spin" /> : <>Start {trial.trial_days}-day free trial — no card needed</>}
                  </button>
                )}
                {trial && !trial.available && trial.reason === "already_claimed" && (
                  <p className="mt-3 text-center text-[11px] text-emerald-100/40">You've already used your free trial.</p>
                )}
              </>
            )}
          </div>

          {/* Feed */}
          <div>
            <div className="mb-4 flex items-center gap-3 rounded-xl border border-lime-400/25 bg-lime-400/5 p-3" data-testid="signals-live-strip">
              <Radio size={16} className="text-lime-300 shrink-0" />
              <p className="text-xs text-emerald-100/80">Live signals stream into your <span className="text-lime-300">dashboard Signals tab</span> and the private Telegram channel the moment you subscribe. Below are sample setups.</p>
            </div>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="overline">Samples</p>
                <h2 className="mt-1 font-editorial text-2xl font-medium text-white">Example setups</h2>
              </div>
              <span className="chip chip-neutral">examples</span>
            </div>
            <p className="mb-4 text-xs text-emerald-100/60">These are illustrative examples. Live signals are delivered to active subscribers via the private Telegram channel and in your dashboard.</p>

            {feed.posts.length === 0 ? (
              <div className="card-edge p-8">
                <p className="text-sm text-emerald-100/60">
                  Example setups are on the way. Subscribers receive live signals via Telegram and in their dashboard the moment we post them.
                </p>
              </div>
            ) : (
              <div className="grid gap-3">
                {feed.posts.map((s, i) => <SignalCard key={s.id} s={s} idx={i} />)}
                {feedMore && (
                  <button onClick={loadMore} disabled={feedBusy} data-testid="signals-load-more"
                    className="btn-edge-ghost mt-1 w-full justify-center text-xs">
                    {feedBusy ? <Loader2 size={14} className="animate-spin" /> : null} Load more examples
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function SignalCard({ s, idx }) {
  const Dir = s.direction === "SELL" ? TrendingDown : s.direction === "HOLD" ? MinusCircle : TrendingUp;
  const tone = s.direction === "SELL" ? "text-red-300" : s.direction === "HOLD" ? "text-emerald-100/70" : "text-lime-300";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: idx * 0.04 }}
      className="card-edge p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1 font-mono-edge text-xs uppercase tracking-widest ${tone}`}>
              <Dir size={12} /> {s.direction}
            </span>
            {s.symbol && <span className="chip chip-neutral">{s.symbol}</span>}
            {s.outcome && OUTCOMES[s.outcome] && <span className={`chip ${OUTCOMES[s.outcome].cls}`} data-testid={`signal-outcome-${s.id}`}>{OUTCOMES[s.outcome].label}</span>}
            {(s.tags || []).map((t) => <span key={t} className="chip chip-emerald">{t}</span>)}
          </div>
          <h3 className="mt-2 font-editorial text-lg font-medium text-white">{s.title}</h3>
        </div>
        <span className="font-mono-edge text-[10px] uppercase tracking-widest text-emerald-100/40">
          {new Date(s.published_at).toLocaleString()}
        </span>
      </div>
      {s.locked ? (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-dashed border-lime-400/25 bg-lime-400/5 p-3 text-sm text-lime-200/80">
          <Lock size={14} /> Body, entry, SL and TP unlock with an active subscription.
        </div>
      ) : (
        <div className="mt-3">
          {s.body && <p className="text-sm text-emerald-100/80">{s.body}</p>}
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            {s.entry && <Stat k="Entry" v={s.entry} />}
            {s.sl && <Stat k="SL" v={s.sl} />}
            {s.tp && <Stat k="TP" v={s.tp} />}
          </div>
        </div>
      )}
    </motion.div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="rounded-lg border border-emerald-500/12 bg-[#0d2416]/50 p-2.5">
      <p className="font-mono-edge text-[10px] uppercase tracking-widest text-emerald-100/50">{k}</p>
      <p className="text-sm text-white">{v}</p>
    </div>
  );
}
